import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Form, Input, Select, Button, Modal, Spin, Tooltip } from "antd";
import { UserProfile, ExternalErrorType } from "lib/types";
import { showToast, PhoneInput } from "ui/components/common";
import { SvgGoogle, SvgMicrosoft } from "ui/svgs";
import TopNav from "ui/dashboard/topnav";
import momentTimezone from "moment-timezone";
import {
  getUserProfile,
  updateUserProfile,
  unBindSso,
  changePwd,
} from "./service";
import { sendResetEmail } from "lib/actions";
import { openSSOWindow, colorHash, validatePhoneNumber } from "lib/utils";
import { SSOType, SSOPage, SSOPostData } from "pages/dashboard/sso/page";
import "react-phone-input-2/lib/style.css";
import { text } from "stream/consumers";
const { Option } = Select;

export default function Profile() {
  const [form] = Form.useForm();
  const phoneInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [pwdChangeSubmitting, setPwdChangeSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChangeOwdOpen, setIsChangeOwdOpen] = useState(false);

  const [profile, setProfile] = useState<UserProfile>();

  const [disconnectingGoogle, setDisconnectingGoogle] = useState(false);
  const [disconnectingMicrosoft, setDisconnectingMicrosoft] = useState(false);

  const microsoftWindow = useRef<Window | null>(null);
  const googleWindow = useRef<Window | null>(null);

  const [phoneCountry, setPhoneCountry] = useState<string>(null);

  useEffect(() => {
    getProfile();
  }, []);

  useEffect(() => {
    const ssoCallBack = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      console.log("Received message:", event.data);
      try {
        const data = JSON.parse(event.data) as SSOPostData;
        // @ts-ignore
        const keepSsoWindow = window.keepSsoWindow;
        if (data?.ssoType === SSOType.MicrosoftSSO) {
          !keepSsoWindow && microsoftWindow.current?.close();
        } else if (data?.ssoType === SSOType.GoogleSSO) {
          !keepSsoWindow && googleWindow.current?.close();
        } else {
          return;
        }
        if (data.success) {
          const info = profile.ssoInfoList?.find(
            (p) => p.ssoType === data?.ssoType,
          );
          info && (info.bindStatus = "sso_bound");
          setProfile({ ...profile });
          showToast(0, "Connect successfully.", { minWidth: 300 });
          getProfile();
        } else {
          showToast(1, "Failed to connect. Please try again later.");
        }
      } catch (error) {
        console.warn("Failed to parse data", error);
        return;
      }
    };
    window.addEventListener("message", ssoCallBack);
    return () => {
      window.removeEventListener("message", ssoCallBack);
    };
  }, [profile]);

  const getProfile = async () => {
    try {
      setIsLoading(true);

      const res = await getUserProfile();

      if (res.status !== 200) {
        showToast(1, "Failed to fetch profile. Please try again later.");
        setIsLoading(false);
        return;
      }

      const data = res?.data as UserProfile;
      setProfile(data);
    } catch (error) {
      console.error("Failed to fetch profile", error);
      showToast(1, "Failed to fetch profile. Please try again later.");
    }

    setIsLoading(false);
  };

  const onFinish = async (formData: FormData) => {
    console.log("Form values:", formData);

    setSubmitting(true);
    const response = await updateUserProfile(formData);
    setSubmitting(false);
    if (response.status !== 200) {
      showToast(1, "Failed to update profile. Please try again later.");
      return;
    }
    showToast(0, "Profile updated successfully.");
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    setIsChangeOwdOpen(true);
  };

  const handleInitPassword = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.set("email", profile?.email);
    const response = await sendResetEmail(formData);
    if (!response.message) {
      showToast(
        0,
        "Init Password Email has been resent. Please go to your mailbox to continue",
        { minWidth: 800 },
      );
    }
  };

  // const loginGoogle = useGoogleLogin({
  //   onSuccess: handleConnectGoogle,
  //   flow: "auth-code",
  //   onError: () => {
  //     showToast(1, "Failed to authenticate with Microsoft");
  //     setConnectingGoogle(false);
  //   },
  // });

  const handleConnect = async (type: SSOType) => {
    //setConnectingMicrosoft(true);
    if (type === SSOType.GoogleSSO) {
      googleWindow.current = openSSOWindow(SSOPage.Profile, type);
    } else if (type === SSOType.MicrosoftSSO) {
      microsoftWindow.current = openSSOWindow(SSOPage.Profile, type);
    }
    // if (!autoCode) {
    //   console.error("Missing code in response");
    //   showToast(1, "Failed to authenticate");
    //   setConnectingMicrosoft(false);
    //   return;
    // }
    // const respongse = await bindSso(autoCode as string, type);
    // setConnectingMicrosoft(false);

    // if (respongse.status !== 200) {
    //   showToast(1, "Failed to bind. Please try again later.");
    //   return;
    // }
    // const info = profile.ssoInfoList?.find(
    //   (p) => p.ssoType === SSOType.MicrosoftSSO,
    // );
    // info && (info.bindStatus = "sso_bound");
    // setProfile({ ...profile });
    // showToast(0, "Connect successfully.", { minWidth: 300 });
  };

  const handleDisconnect = async (ssoType: string) => {
    ssoType === SSOType.GoogleSSO
      ? setDisconnectingGoogle(true)
      : setDisconnectingMicrosoft(true);
    const response = await unBindSso(ssoType);
    ssoType === SSOType.GoogleSSO
      ? setDisconnectingGoogle(false)
      : setDisconnectingMicrosoft(false);

    if (response.status !== 200) {
      const externalData = response?.data as ExternalErrorType;
      if (externalData.lastThirdPartWithoutPwd) {
        showToast(1, "Failed to disconnect. Please Init Password First.");
        return;
      }
      showToast(1, "Failed to disconnect. Please try again later.");
      return;
    }

    const info = profile.ssoInfoList?.find((p) => p.ssoType === ssoType);
    info && (info.bindStatus = "sso_unbound") && (info.thirdPartyAccount = "");
    setProfile({ ...profile });
    showToast(0, "Disconnected successfully.", { minWidth: 250 });
  };

  const filterTimeZones = useMemo(() => {
    const timeZonesOption = momentTimezone.tz.names().map((timeZone) => {
      const offset = momentTimezone.tz(timeZone).format("Z");
      return {
        value: timeZone,
        label: timeZone + "(" + offset + ")",
      };
    });

    const slashIndex = momentTimezone.tz.guess().indexOf("/");
    const currentState = momentTimezone.tz.guess().slice(0, slashIndex);
    const filterTimeZones = timeZonesOption.filter((timeZone) => {
      return timeZone.label.includes(currentState);
    });
    return filterTimeZones;
  }, []);

  const handleChangePwd = async (formData: FormData) => {
    setPwdChangeSubmitting(true);
    console.log("Form values:", formData);

    const reponse = await changePwd(
      profile?.email,
      formData["password"],
      formData["oldPassword"],
    );
    setIsChangeOwdOpen(false);
    setPwdChangeSubmitting(false);
    if (reponse.status !== 200) {
      showToast(1, "Failed to Change Password. Please try again later.");
      return;
    }
    showToast(0, "Change Password successfully.");
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center h-full mr-6 mb-6 ">
        <TopNav title="Profile" />
        <div className="flex flex-col gap-8 items-center justify-center h-full w-full  md:overflow-hidden bg-white rounded-3xl">
          <div
            className="ml-1 text-blue-500  inline-block h-9 w-9 animate-spin rounded-full border-[3px] border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status"
          ></div>
        </div>
      </div>
    );
  }
  if (!profile) {
    return "Data error.";
  }
  const googleBindInfo = profile.ssoInfoList?.find(
    (p) => p.ssoType === SSOType.GoogleSSO,
  );
  const microsoftBindInfo = profile.ssoInfoList?.find(
    (p) => p.ssoType === SSOType.MicrosoftSSO,
  );

  return (
    <div
      className="profile-page flex flex-col items-center h-full mr-6 mb-6"
      style={{ height: "calc(100% - 24px)" }}
    >
      <TopNav title="Profile" />
      <div className="flex flex-col gap-8 items-center w-full  overflow-y-auto bg-white rounded-3xl pb-6  flex-grow">
        <div className="w-[600px] bg-[#FAFAFA] rounded-2xl px-6 pt-6 mt-6">
          <p className="font-semibold mb-4">Account Information</p>
          <Form
            form={form}
            name="profile"
            layout="vertical"
            onFinish={onFinish}
            //className="md:w-[600px]"
            initialValues={{
              timezone: profile.timezone,
              orgTimezone: profile.orgTimezone,
              firstName: profile.firstName,
              lastName: profile.lastName,
              email: profile.email,
              phoneNumber: profile.phoneNumber,
            }}
          >
            <div className="flex gap-10">
              <div className="flex-grow">
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[
                    { required: true, message: "Please input the first name!" },
                  ]}
                >
                  <Input className="h-[54px] rounded-2xl border-[#BDBDBD]" />
                </Form.Item>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[
                    { required: true, message: "Please input the last name!" },
                  ]}
                >
                  <Input className="h-[54px] rounded-2xl border-[#BDBDBD]" />
                </Form.Item>
              </div>
              <div className="w-[140px] rounded-full mt-4">
                <div
                  className="w-full h-[140px] rounded-full flex items-center justify-center text-[60px] font-semibold text-white"
                  style={{
                    backgroundColor: colorHash.hex(
                      profile.firstName + " " + profile.lastName,
                    ),
                  }}
                >
                  {profile?.firstName?.at(0)?.toUpperCase() || ""}
                </div>
                <div className="text-center mt-3">
                  <p className="inline-block  px-3 py-1 bg-[#FFF7D6] text-[#974F0C] rounded-md font-semibold">
                    {profile?.role}
                  </p>
                </div>
              </div>
            </div>
            <Form.Item label="Email" name="email">
              <Input
                readOnly
                className="h-[54px] rounded-2xl border-[#BDBDBD]"
              />
            </Form.Item>
            <Form.Item
              label="Phone Number"
              name="phoneNumber"
              rules={[
                {
                  validator: (_, value) => {
                    if (validatePhoneNumber(value)) {
                      return Promise.resolve();
                    } else {
                      return Promise.reject(new Error("Invalid phone number!"));
                    }
                  },
                },
              ]}
            >
              <div className="h-[54px]">
                <PhoneInput
                  value={
                    form.getFieldValue("phoneNumber") || profile.phoneNumber
                  }
                  ref={phoneInputRef}
                  style={{ borderRadius: 16 }}
                  onChange={(phone, countryData) => {
                    setPhoneCountry(
                      (countryData as { countryCode: string })?.countryCode,
                    );
                    form.setFieldsValue({ phoneNumber: phone });
                  }}
                />
              </div>

              {/* <Input className="h-[54px] rounded-2xl border-[#BDBDBD]" /> */}
            </Form.Item>

            <Form.Item
              label="User Time Zone"
              name="timezone"
              rules={[{ message: "Please select a time zone!" }]}
            >
              <Select
                showSearch
                placeholder="Select a time zone"
                className="h-[54px] rounded-2xl"
              >
                {filterTimeZones?.map((timeZone) => (
                  <Option value={timeZone.value} key={timeZone.value}>
                    {timeZone.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {Object.prototype.hasOwnProperty.call(profile, "orgTimezone") && (
              <Form.Item
                label="Org Time Zone"
                name="orgTimezone"
                rules={[{ message: "Please select a time zone!" }]}
              >
                <Select
                  showSearch
                  placeholder="Select a time zone"
                  className="h-[54px] rounded-2xl"
                >
                  {filterTimeZones?.map((timeZone) => (
                    <Option value={timeZone.value} key={timeZone.value}>
                      {timeZone.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item>
              {profile?.pwdIsSet ? (
                <Button
                  className="float-left flex justify-center h-[54px] w-[185px] items-center rounded-2xl border-blue-500 px-6 text-base font-medium text-blue-500 transition-colors "
                  onClick={handleChangePassword}
                >
                  Change Password
                </Button>
              ) : (
                <Button
                  className="float-left flex justify-center h-[54px] w-[160px] items-center rounded-2xl border-blue-500 px-6 text-base font-medium text-blue-500 transition-colors "
                  onClick={handleInitPassword}
                >
                  Init Password
                </Button>
              )}

              <Button
                type="primary"
                htmlType="submit"
                disabled={submitting}
                className="float-right flex justify-center h-[54px] w-[100px] items-center rounded-2xl bg-blue-500 px-6 text-base font-medium text-white transition-colors "
              >
                Save
              </Button>
            </Form.Item>
          </Form>
        </div>
        <div className="w-[600px] bg-[#FAFAFA] rounded-2xl px-6 py-6 mt-6 h-fit font-semibold">
          <p>SSO Settings</p>
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-2 font-semibold w-32">
              <SvgGoogle width="21" />
              Google
            </div>
            <p className="w-64 font-normal text-center">
              {googleBindInfo?.bindStatus === "sso_bound" ? (
                <Tooltip title={googleBindInfo?.thirdPartyAccount}>
                  <p className="overflow-x-hidden break-words text-ellipsis text-nowrap break-all">
                    {googleBindInfo?.thirdPartyAccount}
                  </p>
                </Tooltip>
              ) : (
                "Not connected"
              )}
            </p>
            <div className="w-32">
              {googleBindInfo?.bindStatus === "sso_bound" ? (
                <button
                  className="float-right font-semibold rounded-xl w-[113px] h-[41px] flex items-center justify-center border border-[#F73D2F] text-[#F73D2F] hover:border-[#f76359] hover:text-[#f76359]"
                  onClick={() => handleDisconnect(SSOType.GoogleSSO)}
                  disabled={disconnectingGoogle}
                >
                  {disconnectingGoogle ? (
                    <div
                      className="ml-1 text-[#F73D2F]  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                      role="status"
                    ></div>
                  ) : (
                    "Disconnect"
                  )}
                </button>
              ) : (
                <button
                  className="float-right font-semibold rounded-xl w-[93px] h-[41px] flex items-center justify-center border border-[#3E83F6] text-[#3E83F6] hover:border-[#5590f7] hover:text-[#5590f7]"
                  onClick={() => {
                    //setConnectingGoogle(true);
                    handleConnect(SSOType.GoogleSSO);
                  }}
                >
                  Connect
                </button>
              )}
            </div>
          </div>
          <div className="flex justify-between items-center  mt-4">
            <div className="flex items-center gap-2 font-semibold w-32">
              <SvgMicrosoft width="21" />
              Microsoft
            </div>
            <p className="w-64 font-normal text-center">
              {microsoftBindInfo?.bindStatus === "sso_bound" ? (
                <Tooltip title={microsoftBindInfo?.thirdPartyAccount}>
                  <p className="overflow-x-hidden break-words text-ellipsis text-nowrap break-all">
                    {microsoftBindInfo?.thirdPartyAccount}
                  </p>
                </Tooltip>
              ) : (
                "Not connected"
              )}
            </p>
            <div className="w-32">
              {microsoftBindInfo?.bindStatus === "sso_bound" ? (
                <button
                  className="float-right font-semibold rounded-xl w-[113px] h-[41px] flex items-center justify-center border border-[#F73D2F] text-[#F73D2F] hover:border-[#f76359] hover:text-[#f76359]"
                  onClick={() => handleDisconnect(SSOType.MicrosoftSSO)}
                  disabled={disconnectingMicrosoft}
                >
                  {disconnectingMicrosoft ? (
                    <div
                      className="ml-1 text-[#F73D2F]  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                      role="status"
                    ></div>
                  ) : (
                    "Disconnect"
                  )}
                </button>
              ) : (
                <button
                  className="float-right font-semibold rounded-xl w-[93px] h-[41px] flex items-center justify-center border border-[#3E83F6] text-[#3E83F6] hover:border-[#5590f7] hover:text-[#5590f7]"
                  onClick={() => {
                    // setConnectingMicrosoft(true);
                    handleConnect(SSOType.MicrosoftSSO);
                  }}
                >
                  Connect
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal open={isChangeOwdOpen} footer={null} centered closable={false}>
        <Form
          name="change_password"
          onFinish={handleChangePwd}
          layout="vertical"
        >
          <Form.Item
            name="oldPassword"
            label="Old Password"
            className=""
            rules={[
              {
                required: true,
                message: "Please input your old password!",
              },
            ]}
            hasFeedback
          >
            <Input.Password className="h-[54px] rounded-2xl" />
          </Form.Item>
          <Form.Item
            name="password"
            label="New Password"
            className=""
            rules={[
              {
                required: true,
                message: "Please input your password!",
              },
            ]}
            hasFeedback
          >
            <Input.Password className="h-[54px] rounded-2xl" />
          </Form.Item>

          <Form.Item
            name="confirm"
            label="Confirm Password"
            dependencies={["password"]}
            hasFeedback
            rules={[
              {
                required: true,
                message: "Please confirm your password!",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("The two passwords do not match!"),
                  );
                },
              }),
            ]}
          >
            <Input.Password className="h-[54px] rounded-2xl" />
          </Form.Item>

          <Form.Item>
            <Button
              //type="primary"
              htmlType="submit"
              disabled={pwdChangeSubmitting}
              className="primaryButton float-right"
            >
              Change Password
            </Button>
            <Button
              className="primaryButton bordered float-right mr-6"
              onClick={() => {
                setIsChangeOwdOpen(false);
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
